import * as Tabs from '@radix-ui/react-tabs'
import {
  AlertTriangle,
  Ban,
  CalendarClock,
  CheckCircle2,
  // 「今週の状況」の折りたたみ用
  ChevronDown,
  ChevronRight,
  CircleSlash2,
  Clock3,
  Copy,
  FileClock,
  GitCompareArrows,
  History,
  HelpCircle,
  ListChecks,
  Maximize2,
  Minimize2,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Badge, Card, CardContent } from '@/components'
// 会議ボード連携
import { BoardCohortPanels, BoardMaturedCohortBoard, useSnapshotWithCohort, type CohortPanelVariant } from '@/components/BoardCohortPanels'
import { useBoardSection } from '@/hooks/useMeetingBoard'
import { BoardPreviousMeetingSection, useMeetingTabs, useMeetingTabsLoading, useTabOwners, type MeetingTabOwner } from '@/components/BoardPreviousMeeting'
import { BoardProgressBoard } from '@/components/BoardProgressBoard'
import { useSnapshotWithWorkstreams } from '@/components/BoardWorkstreams'
import { BoardLandingTable, BoardMetricGrid, BoardPendingTable, BoardPlannedMetrics, useTabMetrics } from '@/components/BoardMetricGrid'
import {
  useMeetingCenterHistory,
  useMeetingCenterLatest,
  useMeetingCenterSnapshot,
} from '@/hooks/useMeetingCenter'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import { ApiError } from '@/lib/api'
import { meetingUnknownReasonPresentation } from '@/lib/meeting-center-presentation'
import { cn } from '@/lib/utils'
import type {
  EvidenceBackedText,
  EvidenceRef,
  HandoffReadiness,
  MeetingAudience,
  MeetingEvidenceState,
  MeetingScheduleState,
  MeetingSnapshot,
  MeetingSnapshotLifecycle,
  OutcomeGateSet,
  RuntimeHealth,
  SourceRun,
  Workstream,
} from '@/types/meeting-center'

// 'board' = 専用パネルを持つ会議のタブ
type BaseMeetingTab = 'current' | 'actions' | 'sources' | 'history' | 'board'
export type MeetingCenterSurface = 'weekly-status' | 'meeting-center' | 'evidence-history'

const PRESENTATION_FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const SURFACE_COPY: Record<
  MeetingCenterSurface,
  { title: string; description: string; defaultTab: BaseMeetingTab }
> = {
  'weekly-status': {
    title: '現在の状況',
    // 会議ごとの深掘りは「週次会議」に寄せ、ここは今の状況だけにする
    description: '今やること と、取組ごとの現在地をまとめて確認します。会議で使う資料は「週次会議」へ。',
    defaultTab: 'current',
  },
  'meeting-center': {
    title: '週次会議',
    description: '会議ごとに前回のふりかえりと現況を確認し、今週の判断と担当を決めます。',
    // 「今週」タブは今週の状況に寄せたので、既定は最初の会議タブ
    defaultTab: 'board',
  },
  'evidence-history': {
    title: '根拠・履歴',
    description: '情報源の取得状態、確認時刻、過去の改訂を確認します。',
    defaultTab: 'sources',
  },
}

export function buildMeetingQuestionPrompt(
  snapshot: MeetingSnapshot,
  audience: MeetingAudience,
): string {
  const perspective = audience === 'executive' ? '役員会議' : '現場会議'
  const decisions = snapshot.decisions
    .filter((decision) => decision.state === 'proposed')
    .slice(0, 3)
    .map((decision, index) => `${index + 1}. ${decision.summary}`)
  const workstreams = snapshot.workstreams
    .map((workstream) => `- ${workstream.title}: 日程=${SCHEDULE_LABELS[workstream.scheduleState]}`)

  return [
    `${snapshot.weekOf}の${perspective}について確認してください。`,
    '次の取組ごとに、今週決めること、未解決事項、担当と期限の不足を指摘してください。',
    ...workstreams,
    ...(decisions.length > 0 ? ['判断候補:', ...decisions] : ['判断候補: 未登録']),
    '根拠が確認できない項目は推測せず「未確認」としてください。',
  ].join('\n')
}

function workstreamTabValue(workstreamId: string): string {
  return `workstream:${workstreamId}`
}

const LIFECYCLE_LABELS: Record<MeetingSnapshotLifecycle, string> = {
  draft: '下書き',
  ready: '会議準備済み',
  locked: '確定',
  superseded: '改訂済み',
  blocked: '準備停止中',
}

const SCHEDULE_LABELS: Record<MeetingScheduleState, string> = {
  on_track: '予定どおり',
  attention: '要確認',
  blocked: '停止中',
  unknown: '未確認',
}

const STATE_LABELS: Record<MeetingEvidenceState, string> = {
  verified: '確認済み',
  stale: '更新期限超過',
  unknown: '未確認',
  absent: '存在なし',
  not_configured: '未設定',
  denied: '権限により非表示',
  error: '取得エラー',
  conflict: '情報が不一致',
}

const DISPLAY_TIME_ZONE = 'Asia/Tokyo'

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00+09:00`)
  if (Number.isNaN(date.getTime())) return '対象週不明'
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date)
}

/**
 * LOCAL-ONLY: 何日前かを返す（判定不能は null）。
 * 会議のまとめが止まっていることを、見た人が自分で日付を引き算せずに分かるようにする。
 */
function daysSince(value: string | null | undefined, now: Date): number | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const diff = Math.floor((now.getTime() - date.getTime()) / 86_400_000)
  return diff < 0 ? null : diff
}

/** これを超えたら「止まっている」として色を変える日数 */
const SNAPSHOT_STALE_DAYS = 14

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '時刻未確認'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '時刻未確認'
  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date)
}

function currentJstWeekOf(reference = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: DISPLAY_TIME_ZONE,
  }).formatToParts(reference)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value
  const year = Number(part('year'))
  const month = Number(part('month'))
  const day = Number(part('day'))
  const calendarDate = new Date(Date.UTC(year, month - 1, day))
  const daysSinceMonday = (calendarDate.getUTCDay() + 6) % 7
  calendarDate.setUTCDate(calendarDate.getUTCDate() - daysSinceMonday)
  return calendarDate.toISOString().slice(0, 10)
}

function latestSourceSuccessfulAt(snapshot: MeetingSnapshot): string | null {
  const timestamps = snapshot.sourceRuns
    .map((run) => run.lastSuccessfulAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value))
  if (timestamps.length === 0) return null
  return new Date(Math.max(...timestamps)).toISOString()
}

function stateTone(state: MeetingEvidenceState): 'success' | 'warning' | 'destructive' | 'muted' {
  if (state === 'verified' || state === 'absent') return 'success'
  if (state === 'stale' || state === 'unknown' || state === 'not_configured') return 'warning'
  if (state === 'denied') return 'muted'
  return 'destructive'
}

function StateIcon({ state, className }: { state: MeetingEvidenceState; className?: string }) {
  const props = { className: cn('h-4 w-4 shrink-0', className), 'aria-hidden': true }
  if (state === 'verified') return <CheckCircle2 {...props} />
  if (state === 'stale') return <Clock3 {...props} />
  if (state === 'unknown') return <HelpCircle {...props} />
  if (state === 'absent') return <CircleSlash2 {...props} />
  if (state === 'not_configured') return <FileClock {...props} />
  if (state === 'denied') return <Ban {...props} />
  if (state === 'conflict') return <GitCompareArrows {...props} />
  return <XCircle {...props} />
}

function StateBadge({ state }: { state: MeetingEvidenceState }) {
  return (
    <Badge tone={stateTone(state)} className="rounded-md">
      <StateIcon state={state} />
      {STATE_LABELS[state]}
    </Badge>
  )
}

function ScheduleBadge({ state }: { state: MeetingScheduleState }) {
  const tone = state === 'on_track'
    ? 'success'
    : state === 'attention' || state === 'unknown'
      ? 'warning'
      : 'destructive'
  return (
    <Badge tone={tone} className="rounded-md">
      {state === 'on_track' ? <CheckCircle2 className="h-4 w-4" aria-hidden /> : <AlertTriangle className="h-4 w-4" aria-hidden />}
      {SCHEDULE_LABELS[state]}
    </Badge>
  )
}

function EvidenceBackedSchedule({
  workstream,
  onOpenSources,
}: {
  workstream: Workstream
  onOpenSources: () => void
}) {
  const resolved = resolveClaim(workstream.schedule, workstream.evidenceRefs)
  if (!resolved.displayable) {
    return (
      <div className="space-y-1">
        <StateBadge state={resolved.state} />
        <p className="text-xs text-muted-foreground">{resolved.reasonLabel}</p>
        {resolved.evidenceCount > 0 && (
          <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
        )}
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <ScheduleBadge state={workstream.scheduleState} />
      {resolved.state !== 'verified' && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StateBadge state={resolved.state} />
          <span>{resolved.reasonLabel}</span>
          <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
        </div>
      )}
    </div>
  )
}

/**
 * タブの担当。
 *
 * 会議 snapshot 側に担当が入っていればそれを出し、無ければ配信側のタブ定義から出す。
 * 利用者として登録されていない担当は、名前は出すが「未登録」と添える。
 * 名前だけ出して登録済みと区別しないと、後で「見えているのに操作できない」理由が
 * 誰にも分からなくなる。
 */
function TabOwners({
  owners,
  fallback,
}: {
  owners: readonly MeetingTabOwner[]
  fallback: string
}) {
  if (owners.length === 0) return <>担当 {fallback}</>
  const unregistered = owners.filter((owner) => owner.memberRef === null).length
  return (
    <>
      担当 {owners.map((owner) => owner.displayName).join('・')}
      {unregistered > 0 && (
        <span className="ml-2 tabular-nums">うち {unregistered}名は利用者未登録</span>
      )}
    </>
  )
}

function ownerLabel(ownerRef: string | null, ownerDisplayName?: string | null): string {
  if (ownerDisplayName?.trim()) return ownerDisplayName.trim()
  return ownerRef ? '担当設定済み' : '担当未設定'
}

interface ProgressSummary {
  completed: number
  total: number
  percent: number | null
  approved: boolean
  evidenceCount: number
  completedWeight: number
  totalWeight: number
  partial: boolean
}

function progressForGateSet(
  gateSet: OutcomeGateSet,
  workstream: Workstream,
): ProgressSummary {
  const active = gateSet.gates.filter((gate) => gate.active)
  const denominatorIsApproved = Boolean(
    gateSet.approved &&
    gateSet.version &&
    gateSet.approvedBy &&
    gateSet.approvedAt &&
    gateSet.definitionHash &&
    active.length > 0,
  )
  if (!denominatorIsApproved) {
    return {
      completed: 0,
      total: active.length,
      percent: null,
      approved: false,
      evidenceCount: 0,
      completedWeight: 0,
      totalWeight: 0,
      partial: false,
    }
  }

  const evidenceById = new Map(
    workstream.evidenceRefs.map((item) => [item.evidenceId, item] as const),
  )
  const completed = active.filter((gate) =>
    gate.status === 'completed' &&
    gate.completionEvidenceIds.length > 0 &&
    gate.completionEvidenceIds.every(
      (evidenceId) => evidenceById.get(evidenceId)?.state === 'verified',
    ),
  )
  const totalWeight = active.reduce((sum, gate) => sum + gate.weight, 0)
  const completedWeight = completed.reduce((sum, gate) => sum + gate.weight, 0)
  const percent = totalWeight > 0
    ? Math.round((completedWeight / totalWeight) * 10_000) / 100
    : null

  return {
    completed: completed.length,
    total: active.length,
    percent,
    approved: true,
    evidenceCount: new Set(completed.flatMap((gate) => gate.completionEvidenceIds)).size,
    completedWeight,
    totalWeight,
    partial: false,
  }
}

function aggregateProgress(snapshot: MeetingSnapshot): ProgressSummary {
  const allSummaries = snapshot.workstreams
    .map((workstream) => progressForGateSet(workstream.outcomeGateSet, workstream))
  const summaries = allSummaries.filter((summary) => summary.approved)
  const partial = allSummaries.some((summary) => !summary.approved)
  if (summaries.length === 0) {
    return {
      completed: 0,
      total: 0,
      percent: null,
      approved: false,
      evidenceCount: 0,
      completedWeight: 0,
      totalWeight: 0,
      partial,
    }
  }
  const completed = summaries.reduce((sum, summary) => sum + summary.completed, 0)
  const total = summaries.reduce((sum, summary) => sum + summary.total, 0)
  const completedWeight = summaries.reduce((sum, summary) => sum + summary.completedWeight, 0)
  const totalWeight = summaries.reduce((sum, summary) => sum + summary.totalWeight, 0)
  return {
    completed,
    total,
    percent: !partial && totalWeight > 0
      ? Math.round((completedWeight / totalWeight) * 10_000) / 100
      : null,
    approved: true,
    evidenceCount: summaries.reduce((sum, summary) => sum + summary.evidenceCount, 0),
    completedWeight,
    totalWeight,
    partial,
  }
}

function sourceCoverage(snapshot: MeetingSnapshot) {
  return {
    verified: snapshot.requiredVerifiedSourceCount,
    total: snapshot.requiredSourceCount,
  }
}

function sourceSummaryState(snapshot: MeetingSnapshot): MeetingEvidenceState {
  return snapshot.requiredSourceState
}

/**
 * source 種別の表示ラベル。snapshot は opaque な種別キーだけを持つため、
 * 読者向けの名称はここで解決する(実 channel 名・実 URL は保持しない設計)。
 */
const SOURCE_TYPE_LABELS: Record<string, string> = {
  code_hosting: '開発リポジトリ(GitHub)',
  engineering: '開発リポジトリ(GitHub)',
  application_runtime: '本番システムの稼働状態',
  operations: '運用記録・未解決事項',
  collaboration: '社内チャット(Slack/Chatwork)',
  documents: '共有ドキュメント(Drive)',
  memory: '過去の週次記録・決定事項',
  client_surface: '外部向け配信状態',
  conferencing: '会議の録画・文字起こし(Zoom/Meet)',
}

function sourceTypeLabel(sourceType: string | null | undefined): string | null {
  if (!sourceType) return null
  return SOURCE_TYPE_LABELS[sourceType.replace(/-/g, '_')] ?? null
}

interface ResolvedClaim {
  displayable: boolean
  text: string | null
  state: MeetingEvidenceState
  observedAt: string | null
  evidenceCount: number
  reasonLabel: string
  sourceLabels: string[]
}

function resolveClaim(claim: EvidenceBackedText, evidenceRefs: EvidenceRef[]): ResolvedClaim {
  const text = claim.text?.trim() || null
  const evidenceIds = [...new Set(claim.evidenceIds)]
  const evidenceById = new Map(evidenceRefs.map((item) => [item.evidenceId, item] as const))
  const refs = evidenceIds.map((evidenceId) => evidenceById.get(evidenceId))
  const presentRefs = refs.filter((ref): ref is EvidenceRef => Boolean(ref))
  const allResolved = refs.length > 0 && presentRefs.length === refs.length
  const unknownPresentation = claim.unknownReason
    ? meetingUnknownReasonPresentation(claim.unknownReason)
    : null
  const stateOrder: MeetingEvidenceState[] = [
    'conflict',
    'error',
    'denied',
    'stale',
    'unknown',
    'not_configured',
    'absent',
    'verified',
  ]
  const state = unknownPresentation?.state ?? (allResolved
    ? stateOrder.find((candidate) => presentRefs.some((ref) => ref.state === candidate)) ?? 'unknown'
    : 'unknown')
  const displayable = Boolean(
    !unknownPresentation
      && text
      && allResolved
      && (state === 'verified' || state === 'stale' || state === 'conflict'),
  )
  const stateReason: Partial<Record<MeetingEvidenceState, string>> = {
    stale: '根拠が更新期限を超えています',
    conflict: '複数の根拠が一致していません',
    error: '根拠を取得できていません',
    denied: '根拠は権限により非表示です',
    not_configured: '必要な情報源が未設定です',
    absent: '対象の存在を確認できません',
    unknown: '確認できる根拠がありません',
  }

  const sourceLabels = [...new Set(
    presentRefs
      .map((ref) => sourceTypeLabel(ref.sourceType))
      .filter((label): label is string => Boolean(label)),
  )]

  if (!displayable) {
    return {
      displayable: false,
      text: null,
      state,
      observedAt: presentRefs
        .map((ref) => ref.observedAt)
        .filter((value): value is string => Boolean(value))
        .sort()
        .at(-1) ?? null,
      evidenceCount: presentRefs.length,
      reasonLabel: unknownPresentation?.label || stateReason[state] || '確認できる根拠がありません',
      sourceLabels,
    }
  }
  const observedAt = presentRefs
    .map((ref) => ref.observedAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1) ?? null

  return {
    displayable: true,
    text,
    state,
    observedAt,
    evidenceCount: presentRefs.length,
    reasonLabel: stateReason[state] ?? '',
    sourceLabels,
  }
}

/**
 * LOCAL-ONLY: 主要 4 項目（現在地 / 予定 / 課題 / 今週やること）が
 * すべて根拠なしで表示できない取組かどうか。
 *
 * 根拠のない主張は出さないのが CH の設計なので、この状態では画面が正しく空になる。
 * ただし空欄を 4 つ並べると「壊れている」ように見えるため、呼び出し側で
 * 「更新が止まっている」という事実 1 行に置き換えるための判定に使う。
 */
function isDormantWorkstream(workstream: Workstream): boolean {
  const refs = workstream.evidenceRefs
  const claims = [workstream.currentState, workstream.schedule, ...workstream.issues, ...workstream.weeklyAgenda]
  return !claims.some((claim) => resolveClaim(claim, refs).displayable)
}

function EvidenceDrillthrough({
  count,
  onOpenSources,
}: {
  count: number
  onOpenSources: () => void
}) {
  return (
    <button
      type="button"
      onClick={onOpenSources}
      className="rounded-sm text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-px"
    >
      情報源で根拠 {count}件を確認
    </button>
  )
}

function EvidenceBackedValue({
  claim,
  evidenceRefs,
  onOpenSources,
  compact = false,
  allowDrillthrough = true,
  dense = false,
}: {
  claim: EvidenceBackedText
  evidenceRefs: EvidenceRef[]
  onOpenSources: () => void
  compact?: boolean
  allowDrillthrough?: boolean
  /** 根拠が無いときに 1 行で出す（理由は落とさない） */
  dense?: boolean
}) {
  const resolved = resolveClaim(claim, evidenceRefs)
  if (!resolved.displayable) {
    // dense は「なぜ出せないか」を落とさずに 3 行を 1 行へ畳む。
    // 出せない項目が 6 個並ぶ画面で、縦を食うのは事実ではなく余白だった。
    return (
      <div className={cn(dense ? 'flex flex-wrap items-baseline gap-x-2 gap-y-1' : 'space-y-1')}>
        <p className="text-sm font-medium text-yellow-600">根拠未確認</p>
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StateBadge state={resolved.state} />
          <span>{resolved.reasonLabel}</span>
          {allowDrillthrough && resolved.evidenceCount > 0 && (
            <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <p className="text-sm">{resolved.text}</p>
      {(resolved.state !== 'verified' || !compact) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <StateBadge state={resolved.state} />
          {resolved.state !== 'verified' && <span>{resolved.reasonLabel}</span>}
          {!compact && <span className="tabular-nums">確認 {formatDateTime(resolved.observedAt)}</span>}
          {allowDrillthrough
            ? <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
            : <span>根拠 {resolved.evidenceCount}件</span>}
          {resolved.sourceLabels.length > 0 && (
            <span className="text-muted-foreground">出典: {resolved.sourceLabels.join('、')}</span>
          )}
        </div>
      )}
    </div>
  )
}

function EvidenceBackedList({
  items,
  empty,
  evidenceRefs,
  onOpenSources,
  // 確認済みの行は根拠表示を畳んで、本文だけ読めるようにする
  compact = false,
}: {
  items: EvidenceBackedText[]
  empty: string
  evidenceRefs: EvidenceRef[]
  onOpenSources: () => void
  compact?: boolean
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>
  }
  return (
    <ul className="space-y-3">
      {items.map((item, index) => {
        const resolved = resolveClaim(item, evidenceRefs)
        return (
          <li
            key={`${item.text}-${index}`}
            className="flex gap-2"
            data-evidence-list-state={resolved.state}
          >
            <StateIcon
              state={resolved.state}
              className={cn(
                'mt-0.5 text-primary',
                resolved.state !== 'verified' && 'text-yellow-600',
              )}
            />
            <EvidenceBackedValue
              claim={item}
              evidenceRefs={evidenceRefs}
              onOpenSources={onOpenSources}
              compact={compact}
            />
          </li>
        )
      })}
    </ul>
  )
}

function operationalBadge(
  kind: 'runtime' | 'handoff',
  indicator: RuntimeHealth | HandoffReadiness,
) {
  if (kind === 'runtime') {
    const state = (indicator as RuntimeHealth).state
    const label = state === 'healthy' ? '稼働確認済み' : state === 'degraded' ? '要注意' : state === 'down' ? '停止' : '未確認'
    const tone = state === 'healthy' ? 'success' : state === 'down' ? 'destructive' : 'warning'
    return { label, tone } as const
  }
  const state = (indicator as HandoffReadiness).state
  const label = state === 'ready' ? '引継ぎ準備済み' : state === 'attention' ? '要確認' : state === 'blocked' ? '停止中' : '未確認'
  const tone = state === 'ready' ? 'success' : state === 'blocked' ? 'destructive' : 'warning'
  return { label, tone } as const
}

function IndicatorBlock({
  title,
  kind,
  indicator,
  evidenceRefs,
  onOpenSources,
}: {
  title: string
  kind: 'runtime' | 'handoff'
  indicator: RuntimeHealth | HandoffReadiness
  evidenceRefs: EvidenceRef[]
  onOpenSources: () => void
}) {
  const badge = operationalBadge(kind, indicator)
  const resolved = resolveClaim(indicator, evidenceRefs)
  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      {resolved.displayable ? (
        <>
          <Badge tone={badge.tone} className="rounded-md">{badge.label}</Badge>
          <p className="text-sm">{resolved.text}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StateBadge state={resolved.state} />
            {resolved.state !== 'verified' && <span>{resolved.reasonLabel}</span>}
            <span className="tabular-nums">確認 {formatDateTime(resolved.observedAt)}</span>
            <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
          </div>
        </>
      ) : (
        <div>
          <Badge tone="warning" className="rounded-md">未確認</Badge>
          <p className="text-sm font-medium text-yellow-600">根拠未確認</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <StateBadge state={resolved.state} />
            <span>{resolved.reasonLabel}</span>
            {resolved.evidenceCount > 0 && (
              <EvidenceDrillthrough count={resolved.evidenceCount} onOpenSources={onOpenSources} />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ProgressText({
  workstream,
  onOpenSources,
}: {
  workstream: Workstream
  onOpenSources: () => void
}) {
  const progress = progressForGateSet(workstream.outcomeGateSet, workstream)
  if (!progress.approved) {
    return (
      <div>
        <p className="font-medium text-yellow-600">成果ゲート定義中</p>
        <p className="mt-1 text-xs text-muted-foreground">割合は表示できません</p>
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <p className="font-semibold tabular-nums">
        検証済み {progress.completed}/{progress.total}
      </p>
      {progress.percent !== null && (
        <p className="text-xs tabular-nums text-muted-foreground">{progress.percent}%</p>
      )}
      {progress.evidenceCount > 0 && (
        <EvidenceDrillthrough count={progress.evidenceCount} onOpenSources={onOpenSources} />
      )}
    </div>
  )
}

type ComparisonColumn = 'title' | 'progress' | 'schedule' | 'issue' | 'owner'

const COMPARISON_LABELS: Record<ComparisonColumn, string> = {
  title: '取組',
  progress: '成果ゲート',
  schedule: '日程',
  issue: '最大課題',
  owner: '担当',
}

function ComparisonValue({
  column,
  workstream,
  onOpenSources,
}: {
  column: ComparisonColumn
  workstream: Workstream
  onOpenSources: () => void
}) {
  if (column === 'title') return <span className="font-medium">{workstream.title}</span>
  if (column === 'progress') return <ProgressText workstream={workstream} onOpenSources={onOpenSources} />
  if (column === 'schedule') return <EvidenceBackedSchedule workstream={workstream} onOpenSources={onOpenSources} />
  if (column === 'owner') {
    // 「前回動いた人」は本文からの推測だったため廃止（2026-08-09 沖中判断）。
    // 元データの担当が空なら空のまま出す。
    return <span>{ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}</span>
  }
  if (workstream.issues.length === 0) {
    return <span className="text-muted-foreground">課題は未登録です。有無は未確認です</span>
  }
  return (
    <EvidenceBackedValue
      claim={workstream.issues[0]}
      evidenceRefs={workstream.evidenceRefs}
      onOpenSources={onOpenSources}
      compact
    />
  )
}

function WorkstreamComparison({
  snapshot,
  audience,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  audience: MeetingAudience
  onOpenSources: () => void
}) {
  const columns: ComparisonColumn[] = audience === 'executive'
    ? ['title', 'progress', 'schedule', 'issue', 'owner']
    : ['title', 'owner', 'issue', 'schedule', 'progress']
  return (
    <section aria-labelledby="comparison-title" className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id="comparison-title" className="font-semibold">取組の現在地</h2>
        <span className="text-xs text-muted-foreground">成果と日程を分けて表示</span>
      </div>

      <div className="hidden overflow-x-auto rounded-lg border md:block">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="bg-muted/60 text-xs text-muted-foreground">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-4 py-3 font-medium">{COMPARISON_LABELS[column]}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {snapshot.workstreams.map((workstream) => (
              <tr key={workstream.workstreamId}>
                {columns.map((column) => (
                  <td key={column} className={cn('px-4 py-3 align-top', column === 'issue' && 'max-w-xs')}>
                    <ComparisonValue column={column} workstream={workstream} onOpenSources={onOpenSources} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 md:hidden">
        {snapshot.workstreams.map((workstream) => (
          <div key={workstream.workstreamId} className="rounded-lg border bg-card p-4 shadow-sm">
            <h3 className="font-medium">{workstream.title}</h3>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
              {columns.filter((column) => column !== 'title').map((column) => (
                <div key={column} className={column === 'issue' ? 'col-span-2' : undefined}>
                  <dt className="text-xs text-muted-foreground">{COMPARISON_LABELS[column]}</dt>
                  <dd className="mt-1">
                    <ComparisonValue column={column} workstream={workstream} onOpenSources={onOpenSources} />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  )
}

function ExecutiveFocus({
  snapshot,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  onOpenSources: () => void
}) {
  const decisions = snapshot.decisions.filter((item) => item.state === 'proposed').slice(0, 3)
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <section data-testid="executive-focus" className="space-y-3">
      <div className="flex items-center gap-2">
        <ListChecks className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="font-semibold">今週のTop判断</h2>
      </div>
      {decisions.length > 0 ? (
        <ol className="grid gap-2">
          {decisions.map((decision, index) => (
            <li key={decision.decisionId} className="grid grid-cols-[1.75rem_1fr] gap-2 rounded-md border bg-card px-3 py-2 text-sm">
              <span className="font-semibold tabular-nums text-primary">{index + 1}</span>
              <EvidenceBackedValue
                claim={{
                  text: decision.summary,
                  evidenceIds: decision.evidenceIds,
                  unknownReason: decision.unknownReason,
                }}
                evidenceRefs={evidenceRefs}
                onOpenSources={onOpenSources}
              />
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-muted-foreground">今週の判断事項は未登録です</p>
      )}
    </section>
  )
}

function FieldFocus({
  snapshot,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  onOpenSources: () => void
}) {
  const actions = snapshot.actionItems
    .filter((item) => item.status === 'open' || item.status === 'blocked')
    .slice(0, 3)
  const blockers = snapshot.workstreams
    .filter((workstream) => workstream.scheduleState === 'blocked' || workstream.issues.length > 0)
    // 主要項目がすべて根拠なしの取組は「根拠未確認」しか出せない。
    // それを並べても読む人には何も分からないので、ここには出さない
    // （取組タブ側で「更新が止まっています」と 1 行で出している）。
    .filter((workstream) => !isDormantWorkstream(workstream))
    .slice(0, 3)
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <section data-testid="field-focus" className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="font-semibold">今週の担当・期限・課題</h2>
      </div>
      {actions.length === 0 && blockers.length === 0 ? (
        <p className="text-sm text-muted-foreground">未完了アクション・課題は未登録です。有無は未確認です</p>
      ) : (
        <div className="grid gap-2">
          {actions.map((action) => (
            <div key={action.actionItemId} className="rounded-md border bg-card px-3 py-2 text-sm">
              {resolveClaim({ text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }, evidenceRefs).displayable && (
                <div className="mb-2 flex items-start justify-between gap-3">
                  <span className="text-xs text-muted-foreground">
                    {/* 担当は元データの値だけを出す。本文からの推測は「工事日」のような
                        人名でない語を担当者として表示してしまうため廃止（2026-08-09 沖中判断）。 */}
                    担当 {ownerLabel(action.ownerRef, action.ownerDisplayName)}
                    <span className="mx-2" aria-hidden>/</span>
                    期限 <span className="tabular-nums">{formatDateTime(action.dueAt)}</span>
                  </span>
                  <Badge tone={action.status === 'blocked' ? 'destructive' : 'warning'} className="rounded-md">
                    {action.status === 'blocked' ? '停止中' : '未完了'}
                  </Badge>
                </div>
              )}
              <EvidenceBackedValue
                claim={{ text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }}
                evidenceRefs={evidenceRefs}
                onOpenSources={onOpenSources}
              />
            </div>
          ))}
          {blockers.map((workstream) => (
            <div key={workstream.workstreamId} className="rounded-md border bg-card px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground">{workstream.title}の課題・停止要因</p>
              {workstream.issues.length > 0 ? (
                <div className="mt-1">
                  <EvidenceBackedValue
                    claim={workstream.issues[0]}
                    evidenceRefs={workstream.evidenceRefs}
                    onOpenSources={onOpenSources}
                    compact
                  />
                </div>
              ) : (
                <p className="mt-1 text-sm font-medium text-yellow-600">根拠未確認</p>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function CurrentWeekPanel({
  snapshot,
  audience,
  onOpenSources,
  // 「今週の状況」は認知負荷を上げないため、今やること と 現在地 だけに絞る
  compact = false,
}: {
  snapshot: MeetingSnapshot
  audience: MeetingAudience
  onOpenSources: () => void
  compact?: boolean
}) {
  const progress = aggregateProgress(snapshot)
  const coverage = sourceCoverage(snapshot)
  const summaryState = sourceSummaryState(snapshot)
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  const resolvedDelta = snapshot.previousWeekDelta?.text
    ? resolveClaim({
        text: snapshot.previousWeekDelta.text,
        evidenceIds: snapshot.previousWeekDelta.evidenceIds,
        unknownReason: snapshot.previousWeekDelta.unknownReason,
      }, evidenceRefs)
    : null
  const focusPanels = compact
    // 判断（Top判断）は会議で決めることなので「今週の状況」には置かない
    ? [<FieldFocus key="field" snapshot={snapshot} onOpenSources={onOpenSources} />]
    : audience === 'executive'
      ? [<ExecutiveFocus key="executive" snapshot={snapshot} onOpenSources={onOpenSources} />, <FieldFocus key="field" snapshot={snapshot} onOpenSources={onOpenSources} />]
      : [<FieldFocus key="field" snapshot={snapshot} onOpenSources={onOpenSources} />, <ExecutiveFocus key="executive" snapshot={snapshot} onOpenSources={onOpenSources} />]

  return (
    <div className="space-y-6">
      <section className={cn('grid gap-4 border-b pb-6', !compact && 'lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,0.6fr)]')}>
        <div className="grid gap-5">{focusPanels}</div>

        <div className={cn('grid grid-cols-2 gap-x-4 gap-y-3 border-l-0 pt-1 text-sm lg:border-l lg:pl-5', compact && 'hidden')}>
          <div>
            <p className="text-xs text-muted-foreground">成果ゲート</p>
            {progress.approved ? (
              <>
                {progress.partial && <p className="mt-1 font-medium text-yellow-600">一部分母定義中</p>}
                <p className="mt-1 font-semibold tabular-nums">検証済み {progress.completed}/{progress.total}</p>
                {progress.percent !== null && <p className="text-xs tabular-nums text-muted-foreground">{progress.percent}%</p>}
                {progress.evidenceCount > 0 && <EvidenceDrillthrough count={progress.evidenceCount} onOpenSources={onOpenSources} />}
              </>
            ) : (
              <p className="mt-1 font-medium text-yellow-600">定義中</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">情報確認率</p>
            <p className="mt-1 font-semibold tabular-nums">確認済み情報源 {coverage.verified}/{coverage.total}</p>
            <div className="mt-1"><StateBadge state={summaryState} /></div>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-muted-foreground">先週からの変化</p>
            {snapshot.previousWeekDelta && snapshot.previousWeekDelta.state !== 'unknown' && resolvedDelta?.displayable ? (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone={snapshot.previousWeekDelta.state === 'changed' ? 'warning' : 'muted'} className="rounded-md">
                  {snapshot.previousWeekDelta.state === 'changed' ? '変化あり' : '変化なし'}
                </Badge>
                <span>{resolvedDelta.text}</span>
                <span className="text-xs tabular-nums text-muted-foreground">確認 {formatDateTime(snapshot.previousWeekDelta.observedAt)}</span>
                <EvidenceDrillthrough count={resolvedDelta.evidenceCount} onOpenSources={onOpenSources} />
              </div>
            ) : (
              <p className="mt-1 text-yellow-600">前週差分は未確認です</p>
            )}
          </div>
        </div>
      </section>

      {!compact && <WorkstreamComparison snapshot={snapshot} audience={audience} onOpenSources={onOpenSources} />}

      {summaryState !== 'verified' && (
        <div className="flex items-start gap-3 rounded-md border border-yellow-600/30 bg-yellow-600/5 p-3 text-sm" role="status">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-600" aria-hidden />
          <div>
            <p className="font-medium">一部の情報源を確認できていません</p>
            <p className="mt-1 text-muted-foreground">未確認を進捗ゼロへ置き換えず、判断前に状態を確認してください。</p>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * LOCAL-ONLY: 「今週の状況」向けの絞り込み表示。
 * 現在地 / 予定 / 課題 / 今週やること だけを前に出し、定義まわりは畳む。
 * 確認済みの行は根拠表示を出さない（未確認のときだけ黄色で残す）。
 */
function CompactWorkstreamPanel({
  workstream,
  onOpenSources,
  sourceNote,
  stalledSince,
}: {
  workstream: Workstream
  onOpenSources: () => void
  // 会議がまだ紐づいていない取組に、その理由を出す
  sourceNote?: string | null
  // 中身が空の取組に「いつ以降 止まっているか」を出すための基準日
  stalledSince?: string | null
}) {
  const [showDefinition, setShowDefinition] = useState(false)
  // 数字を持つ取組は「現在地」を文章ではなく 数字→目標→進捗 で出す
  const metricsKey = workstream.workstreamId === 'board-panel' ? 'telecom-activation' : workstream.workstreamId
  const tabMetrics = useTabMetrics(metricsKey).data
  const metrics = tabMetrics?.metrics ?? []
  const plannedMetrics = tabMetrics?.plannedMetrics ?? []
  const landingTable = tabMetrics?.landingTable
  const pendingTable = tabMetrics?.pendingTable
  const hasMetrics = metrics.length > 0

  // 数字も無く、主要 4 項目がすべて根拠なしで出せない取組。
  // 空欄を 4 つ並べると「壊れている」ように見えるので、事実（更新が止まっている）だけ出す。
  const dormant = !hasMetrics && plannedMetrics.length === 0 && isDormantWorkstream(workstream)

  const header = (
    <div className="flex flex-col gap-3 border-b px-4 py-4 md:flex-row md:items-start md:justify-between md:px-6">
      <div>
        <h2 className="font-semibold">{workstream.title}</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          担当 {ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}
        </p>
        {sourceNote && <p className="mt-1 text-xs text-yellow-600">{sourceNote}</p>}
      </div>
      {!dormant && (
        <div className="flex flex-wrap items-center gap-2">
          <ProgressText workstream={workstream} onOpenSources={onOpenSources} />
          <EvidenceBackedSchedule workstream={workstream} onOpenSources={onOpenSources} />
        </div>
      )}
    </div>
  )

  if (dormant) {
    return (
      <Card>
        {header}
        <CardContent className="p-4 md:p-6">
          <p className="text-sm text-yellow-700">
            このテーマは
            {stalledSince
              ? <span className="tabular-nums">{formatDate(stalledSince.slice(0, 10))}</span>
              : '前回のまとめ'}
            {' '}以降 更新されていません。
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            前回の記録も「未確認」のままのため、出せる内容がありません。
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      {header}

      <CardContent className="space-y-5 p-4 md:p-6">
        {hasMetrics ? (
          <div className="space-y-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h3 className="text-sm font-medium">現在地</h3>
              {tabMetrics?.asOf && (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {tabMetrics.sourceLabel}・{tabMetrics.asOf.slice(0, 10)} 時点
                </span>
              )}
            </div>
            <BoardMetricGrid metrics={metrics} />
            {pendingTable && <BoardPendingTable table={pendingTable} />}
            {landingTable && <BoardLandingTable table={landingTable} />}
            {/* LOCAL-ONLY: 会議タブと同じ「申込から N 日でそろえた」キャンセルの見方を、こちらにも出す */}
            {workstream.workstreamId === 'board-panel' && <BoardMaturedCohortBoard />}
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <h3 className="text-xs font-medium text-muted-foreground">現在地</h3>
              <div className="mt-1">
                {plannedMetrics.length > 0
                  ? <BoardPlannedMetrics items={plannedMetrics} />
                  : <EvidenceBackedValue claim={workstream.currentState} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} compact />}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-medium text-muted-foreground">予定</h3>
              <div className="mt-1">
                <EvidenceBackedValue claim={workstream.schedule} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} compact />
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-5 border-t pt-5 md:grid-cols-2">
          <section>
            <h3 className="text-sm font-medium">今の課題</h3>
            <div className="mt-2">
              <EvidenceBackedList
                items={workstream.issues}
                empty="課題は未登録です。有無は未確認です"
                evidenceRefs={workstream.evidenceRefs}
                onOpenSources={onOpenSources}
                compact
              />
            </div>
          </section>
          <section>
            <h3 className="text-sm font-medium">今週やること</h3>
            <div className="mt-2">
              <EvidenceBackedList
                items={workstream.weeklyAgenda}
                empty="今週やることは未登録です"
                evidenceRefs={workstream.evidenceRefs}
                onOpenSources={onOpenSources}
                compact
              />
            </div>
          </section>
        </div>

        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowDefinition((v) => !v)}
            aria-expanded={showDefinition}
            className="inline-flex min-h-9 items-center gap-1 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {showDefinition ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
            この取組の定義・稼働状態
          </button>
          {showDefinition && (
            <div className="mt-4 space-y-5">
              <div className="grid gap-3 md:grid-cols-2">
                <IndicatorBlock title="稼働状態" kind="runtime" indicator={workstream.runtimeHealth} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
                <IndicatorBlock title="引継ぎ準備" kind="handoff" indicator={workstream.handoffReadiness} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} />
              </div>
              <dl className="grid gap-x-8 gap-y-5 md:grid-cols-2 xl:grid-cols-4">
                {([
                  ['目的', workstream.purpose],
                  ['手法', workstream.method],
                  ['期待結果', workstream.expectedResult],
                  ['100%の定義', workstream.definitionOf100Percent],
                ] as const).map(([label, claim]) => (
                  <div key={label}>
                    <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
                    <dd className="mt-1">
                      <EvidenceBackedValue claim={claim} evidenceRefs={workstream.evidenceRefs} onOpenSources={onOpenSources} compact />
                    </dd>
                  </div>
                ))}
              </dl>
              <section>
                <h3 className="text-sm font-medium">判断してほしいこと</h3>
                <div className="mt-2">
                  <EvidenceBackedList
                    items={workstream.decisionRequests}
                    empty="判断依頼は未登録です。有無は未確認です"
                    evidenceRefs={workstream.evidenceRefs}
                    onOpenSources={onOpenSources}
                    compact
                  />
                </div>
              </section>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function WorkstreamPanel({
  workstream,
  onOpenSources,
}: {
  workstream: Workstream
  onOpenSources: () => void
}) {
  const refs = workstream.evidenceRefs
  const evidenceCount = refs.length
  const owners = useTabOwners(workstream.workstreamId)

  // 同じ tab の「前回会議のふりかえり」は既に取得済み（同じ query key）なので、
  // ここで参照しても追加のリクエストにはならない。
  const previousMeeting = useBoardSection<{ items: { summary: string }[] }>(
    'previous',
    workstream.workstreamId,
  )
  const inPreviousMeeting = new Set(
    (previousMeeting.data?.items ?? []).map((item) => item.summary),
  )

  // 出せるものを先に、出せないものを後ろに。
  // 「無いものは無いと出す」は保つ（消すと “確認したが無い” と誤読される）が、
  // 読む人が最初に当たるのは中身であるべきなので順番を変える。
  const definitions = ([
    ['100%の定義', workstream.definitionOf100Percent],
    ['現在地', workstream.currentState],
    ['目的', workstream.purpose],
    ['手法', workstream.method],
    ['期待結果', workstream.expectedResult],
    ['スケジュール', workstream.schedule],
  ] as const).map(([label, claim]) => ({ label, claim, shown: resolveClaim(claim, refs).displayable }))
  const orderedDefinitions = [...definitions.filter((d) => d.shown), ...definitions.filter((d) => !d.shown)]

  const indicators = ([
    ['稼働状態', 'runtime', workstream.runtimeHealth],
    ['引継ぎ準備', 'handoff', workstream.handoffReadiness],
  ] as const).map(([title, kind, indicator]) => ({
    title, kind, indicator, shown: resolveClaim(indicator, refs).displayable,
  }))

  // 数字も文章も 1 つも出せない取組。空欄が並ぶ理由を先に 1 行で言う。
  const nothingUsable = definitions.every((d) => !d.shown)
    && indicators.every((i) => !i.shown)
    && workstream.issues.length === 0
    && workstream.weeklyAgenda.length === 0
    && workstream.decisionRequests.length === 0

  return (
    <Card>
      <div className="flex flex-col gap-3 border-b px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
        <div>
          <h2 className="font-semibold">{workstream.title}</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            <TabOwners
              owners={workstream.ownerDisplayName ? [] : owners}
              fallback={ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}
            />
          </p>
        </div>
        {!nothingUsable && (
          <div className="flex flex-wrap items-center gap-2">
            <ProgressText workstream={workstream} onOpenSources={onOpenSources} />
            <EvidenceBackedSchedule workstream={workstream} onOpenSources={onOpenSources} />
          </div>
        )}
      </div>
      <CardContent className="space-y-6 p-4 md:p-6">
        {nothingUsable && (
          <p className="text-sm text-muted-foreground">
            この取組は、まだ会議の内容も数字も CH に入っていません。以下は各項目がなぜ出せないかです。
          </p>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          {indicators.map((i) => (
            <IndicatorBlock
              key={i.title}
              title={i.title}
              kind={i.kind}
              indicator={i.indicator}
              evidenceRefs={refs}
              onOpenSources={onOpenSources}
            />
          ))}
        </div>

        <dl className="grid gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
          {orderedDefinitions.map((d) => (
            <div key={d.label} className={cn(!d.shown && 'flex flex-wrap items-baseline gap-x-2')}>
              <dt className="text-xs font-medium text-muted-foreground">{d.label}</dt>
              <dd className={cn(d.shown && 'mt-1')}>
                <EvidenceBackedValue
                  claim={d.claim}
                  evidenceRefs={refs}
                  onOpenSources={onOpenSources}
                  compact
                  dense
                />
              </dd>
            </div>
          ))}
        </dl>

        <div className="grid gap-6 border-t pt-5 md:grid-cols-2 xl:grid-cols-3">
          {([
            // 「今週の」を外す。ここに出るのは直近会議の内容で、その会議が
            // 何週間か前ということが実際にある（時期は下の「N 時点」で出す）。
            ['現状の課題', workstream.issues, '課題は未登録です。有無は未確認です'],
            ['アジェンダ', workstream.weeklyAgenda, 'アジェンダは未登録です'],
            ['判断してほしいこと', workstream.decisionRequests, '判断依頼は未登録です。有無は未確認です'],
          ] as const).map(([title, items, empty]) => {
            const resolved = items.map((item) => ({ item, r: resolveClaim(item, refs) }))
            // 上の「前回会議のふりかえり」に出ているものを二重に出さない。
            // 同じ文が別の見出し（決定 → 判断してほしいこと）で並ぶと、
            // 決まったことが未決に見える。
            // 本文が出せない行（根拠未確認）は照合対象にならないので、そのまま残す。
            const fresh = resolved.filter(({ r }) => r.text === null || !inPreviousMeeting.has(r.text))
            const duplicated = resolved.length - fresh.length
            const observedAt = fresh
              .map(({ r }) => r.observedAt)
              .filter((value): value is string => typeof value === 'string' && value.length > 0)
              .sort()
              .at(-1)
            return (
              <section key={title}>
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <h3 className="text-sm font-medium">{title}</h3>
                  {observedAt && (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {formatDateTime(observedAt)} 時点
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  {/* 確認済みの行に「確認済み / 確認 日時 / 根拠 1件」を毎行付けると、
                      本文より根拠の飾りの方が長くなる。確認済みは本文だけ出す
                      （未確認の行では理由がそのまま残る）。 */}
                  <EvidenceBackedList
                    items={fresh.map(({ item }) => item)}
                    // 全部が上に出ている場合は「未登録」ではない。下の 1 行が受け皿。
                    empty={duplicated > 0 ? '' : empty}
                    evidenceRefs={refs}
                    onOpenSources={onOpenSources}
                    compact
                  />
                  {duplicated > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      ほか {duplicated}件は上の「前回会議のふりかえり」に出ています
                    </p>
                  )}
                </div>
              </section>
            )
          })}
        </div>

        {nothingUsable && evidenceCount > 0 && (
          <EvidenceDrillthrough count={evidenceCount} onOpenSources={onOpenSources} />
        )}
      </CardContent>
    </Card>
  )
}

function DecisionsActionsPanel({
  snapshot,
  onOpenSources,
}: {
  snapshot: MeetingSnapshot
  onOpenSources: () => void
}) {
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <section className="space-y-3">
        <h2 className="font-semibold">判断事項</h2>
        {snapshot.decisions.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">判断事項は未登録です</p>
        ) : (
          <div className="divide-y rounded-lg border bg-card shadow-sm">
            {snapshot.decisions.map((decision) => {
              const claim = { text: decision.summary, evidenceIds: decision.evidenceIds, unknownReason: decision.unknownReason }
              const resolved = resolveClaim(claim, evidenceRefs)
              return (
                <div key={decision.decisionId} className="space-y-2 p-4">
                  {resolved.displayable && (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">担当 {ownerLabel(decision.ownerRef, decision.ownerDisplayName)}</p>
                      <Badge tone={decision.state === 'decided' ? 'success' : decision.state === 'deferred' ? 'warning' : 'default'} className="rounded-md">
                        {decision.state === 'decided' ? '決定済み' : decision.state === 'deferred' ? '保留' : '判断待ち'}
                      </Badge>
                    </div>
                  )}
                  <EvidenceBackedValue claim={claim} evidenceRefs={evidenceRefs} onOpenSources={onOpenSources} />
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="font-semibold">アクション</h2>
        {snapshot.actionItems.length === 0 ? (
          <p className="rounded-md border p-4 text-sm text-muted-foreground">アクションは未登録です</p>
        ) : (
          <div className="divide-y rounded-lg border bg-card shadow-sm">
            {snapshot.actionItems.map((action) => {
              const claim = { text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }
              const resolved = resolveClaim(claim, evidenceRefs)
              return (
                <div key={action.actionItemId} className="space-y-2 p-4">
                  {resolved.displayable && (
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs text-muted-foreground">
                        担当 {ownerLabel(action.ownerRef, action.ownerDisplayName)}
                        <span className="mx-2" aria-hidden>/</span>
                        期限 <span className="tabular-nums">{formatDateTime(action.dueAt)}</span>
                      </p>
                      <Badge tone={action.status === 'completed' ? 'success' : action.status === 'blocked' ? 'destructive' : action.status === 'cancelled' ? 'muted' : 'warning'} className="rounded-md">
                        {action.status === 'completed' ? '完了' : action.status === 'blocked' ? '停止中' : action.status === 'cancelled' ? '取消' : '未完了'}
                      </Badge>
                    </div>
                  )}
                  <EvidenceBackedValue claim={claim} evidenceRefs={evidenceRefs} onOpenSources={onOpenSources} />
                </div>
              )
            })}
          </div>
        )}
      </section>
    </div>
  )
}

function safeSourceLabel(source: SourceRun, index: number): string {
  if (source.state === 'denied') return '権限により非表示の情報源'
  const typeLabel = sourceTypeLabel(source.sourceType)
  if (source.state === 'error') {
    return typeLabel ? `${typeLabel}(取得エラー)` : '取得エラーの情報源'
  }
  return typeLabel ?? `情報源 ${index + 1}`
}

function SourcesPanel({ snapshot }: { snapshot: MeetingSnapshot }) {
  const deniedCount = snapshot.sourceRuns.filter((run) => run.state === 'denied').length
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold">情報源の鮮度</h2>
        <p className="mt-1 text-sm text-muted-foreground">取得失敗や未設定も消さずに表示します。</p>
      </div>
      {deniedCount > 0 && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm" role="status">
          <ShieldAlert className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
          権限により非表示の情報源があります
        </div>
      )}
      {snapshot.sourceRuns.length === 0 ? (
        <p className="rounded-md border p-4 text-sm text-muted-foreground">情報源の確認履歴はまだありません</p>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {snapshot.sourceRuns.map((source, index) => {
            const restricted = source.state === 'denied'
            return (
              <div key={source.sourceRunId} className="rounded-lg border bg-card p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-medium">{safeSourceLabel(source, index)}</h3>
                  <StateBadge state={source.state} />
                </div>
                {!restricted && (
                  <>
                    {source.purpose?.trim() && (
                      <p className="mt-2 text-sm text-muted-foreground">{source.purpose}</p>
                    )}
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                      <div>
                        <dt className="text-muted-foreground">確認時刻</dt>
                        <dd className="mt-1 tabular-nums">{formatDateTime(source.observedThrough)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">情報源最終成功日時</dt>
                        <dd className="mt-1 tabular-nums">{formatDateTime(source.lastSuccessfulAt)}</dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">根拠件数</dt>
                        <dd className="mt-1 tabular-nums">{source.evidenceCount ?? '未確認'}</dd>
                      </div>
                    </dl>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function HistoricalEvidenceValue({
  claim,
  evidenceRefs,
}: {
  claim: EvidenceBackedText
  evidenceRefs: EvidenceRef[]
}) {
  return (
    <EvidenceBackedValue
      claim={claim}
      evidenceRefs={evidenceRefs}
      onOpenSources={() => undefined}
      allowDrillthrough={false}
    />
  )
}

function historicalGateEvidenceState(
  gate: Workstream['outcomeGateSet']['gates'][number],
  evidenceRefs: EvidenceRef[],
): MeetingEvidenceState {
  if (gate.completionEvidenceIds.length === 0) return 'unknown'
  return resolveClaim({
    text: gate.title,
    evidenceIds: gate.completionEvidenceIds,
    unknownReason: null,
  }, evidenceRefs).state
}

function HistoricalSnapshotContent({ snapshot }: { snapshot: MeetingSnapshot }) {
  const evidenceRefs = snapshot.workstreams.flatMap((workstream) => workstream.evidenceRefs)
  return (
    <div className="space-y-6" data-testid="historical-snapshot-content">
      <header className="space-y-3 border-b pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">{formatDate(snapshot.weekOf)}の週</h3>
            <p className="mt-1 text-xs tabular-nums text-muted-foreground">
              改訂 {snapshot.revision} · 保存 {formatDateTime(snapshot.createdAt)}
            </p>
          </div>
          <Badge tone={snapshot.lifecycle === 'locked' ? 'success' : snapshot.lifecycle === 'blocked' ? 'destructive' : 'muted'} className="rounded-md">
            {LIFECYCLE_LABELS[snapshot.lifecycle]}
          </Badge>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium tabular-nums">
            必須情報源 {snapshot.requiredVerifiedSourceCount}/{snapshot.requiredSourceCount}
          </span>
          <StateBadge state={snapshot.requiredSourceState} />
          <span className="text-xs text-muted-foreground">この改訂で保存された状態</span>
        </div>
      </header>

      <section className="space-y-3" aria-labelledby="historical-workstreams-title">
        <h4 id="historical-workstreams-title" className="text-sm font-semibold">取組の記録</h4>
        <div className="grid gap-4">
          {snapshot.workstreams.map((workstream) => (
            <article key={workstream.workstreamId} className="rounded-md border bg-muted/20 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h5 className="font-medium">{workstream.title}</h5>
                  <p className="mt-1 text-xs text-muted-foreground">
                    担当 {ownerLabel(workstream.ownerRef, workstream.ownerDisplayName)}
                  </p>
                </div>
                <Badge tone={workstream.outcomeGateSet.approved ? 'success' : 'warning'} className="rounded-md">
                  {workstream.outcomeGateSet.approved ? '成果ゲート承認済み' : '成果ゲート定義中'}
                </Badge>
              </div>

              <dl className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">当時の現在地</dt>
                  <dd className="mt-1">
                    <HistoricalEvidenceValue claim={workstream.currentState} evidenceRefs={workstream.evidenceRefs} />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">当時のスケジュール</dt>
                  <dd className="mt-1">
                    <HistoricalEvidenceValue claim={workstream.schedule} evidenceRefs={workstream.evidenceRefs} />
                  </dd>
                </div>
              </dl>

              <div className="mt-4 border-t pt-3">
                <p className="text-xs font-medium text-muted-foreground">成果ゲート</p>
                {workstream.outcomeGateSet.gates.length === 0 ? (
                  <p className="mt-2 text-sm text-muted-foreground">成果ゲートは未登録です</p>
                ) : (
                  <ul className="mt-2 grid gap-2">
                    {workstream.outcomeGateSet.gates.map((gate) => {
                      const evidenceState = historicalGateEvidenceState(gate, workstream.evidenceRefs)
                      return (
                        <li key={gate.gateId} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border bg-card px-3 py-2 text-sm">
                          <span>{gate.title}</span>
                          <span className="flex flex-wrap items-center gap-2">
                            <Badge tone={gate.status === 'completed' ? 'success' : gate.status === 'blocked' ? 'destructive' : 'muted'} className="rounded-md">
                              {gate.status === 'completed' ? '完了' : gate.status === 'blocked' ? '停止中' : '未完了'}
                            </Badge>
                            <StateBadge state={evidenceState} />
                          </span>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <div className="grid gap-5 xl:grid-cols-2">
        <section className="space-y-3" aria-labelledby="historical-decisions-title">
          <h4 id="historical-decisions-title" className="text-sm font-semibold">判断事項</h4>
          {snapshot.decisions.length === 0 ? (
            <p className="text-sm text-muted-foreground">判断事項は未登録です</p>
          ) : snapshot.decisions.map((decision) => (
            <div key={decision.decisionId} className="rounded-md border p-3">
              <HistoricalEvidenceValue
                claim={{ text: decision.summary, evidenceIds: decision.evidenceIds, unknownReason: decision.unknownReason }}
                evidenceRefs={evidenceRefs}
              />
            </div>
          ))}
        </section>
        <section className="space-y-3" aria-labelledby="historical-actions-title">
          <h4 id="historical-actions-title" className="text-sm font-semibold">アクション</h4>
          {snapshot.actionItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">アクションは未登録です</p>
          ) : snapshot.actionItems.map((action) => (
            <div key={action.actionItemId} className="rounded-md border p-3">
              <HistoricalEvidenceValue
                claim={{ text: action.summary, evidenceIds: action.evidenceIds, unknownReason: action.unknownReason }}
                evidenceRefs={evidenceRefs}
              />
            </div>
          ))}
        </section>
      </div>
    </div>
  )
}

function HistoryPanel({
  workspaceId,
}: {
  workspaceId: string
}) {
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null)
  const historyQuery = useMeetingCenterHistory(workspaceId)
  const snapshotQuery = useMeetingCenterSnapshot(workspaceId, selectedSnapshotId)

  if (historyQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">履歴を読み込んでいます</p>
  }
  if (historyQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/20 bg-destructive/5 p-4 text-sm text-destructive">
        履歴を取得できませんでした
      </div>
    )
  }
  const items = historyQuery.data?.items ?? []
  if (items.length === 0) {
    return <p className="rounded-md border p-4 text-sm text-muted-foreground">過去回はまだありません</p>
  }
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(16rem,0.45fr)_minmax(0,1fr)]">
      <div className="self-start divide-y rounded-lg border bg-card shadow-sm">
        {items.map((item) => (
          <button
            key={item.snapshotId}
            type="button"
            onClick={() => setSelectedSnapshotId(item.snapshotId)}
            className={cn(
              'flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring active:translate-y-px',
              selectedSnapshotId === item.snapshotId && 'bg-muted',
            )}
          >
            <span>
              <span className="block font-medium">{formatDate(item.weekOf)}の週</span>
              <span className="mt-1 block text-xs tabular-nums text-muted-foreground">改訂 {item.revision}</span>
            </span>
            <Badge tone={item.lifecycle === 'locked' ? 'success' : item.lifecycle === 'blocked' ? 'destructive' : 'muted'} className="rounded-md">
              {LIFECYCLE_LABELS[item.lifecycle]}
            </Badge>
          </button>
        ))}
      </div>
      <div className="rounded-lg border bg-card p-5 shadow-sm">
        {!selectedSnapshotId ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <History className="h-5 w-5" aria-hidden />
            確認する週を選んでください
          </div>
        ) : snapshotQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">選択した改訂を読み込んでいます</p>
        ) : snapshotQuery.isError || !snapshotQuery.data ? (
          <p className="text-sm text-destructive">選択した改訂を取得できませんでした</p>
        ) : (
          <HistoricalSnapshotContent snapshot={snapshotQuery.data.snapshot} />
        )}
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div aria-busy="true" className="space-y-4" role="status">
      <p className="text-sm text-muted-foreground">週次スナップショットを読み込んでいます</p>
      <div className="h-24 animate-pulse rounded-lg border bg-muted/50 motion-reduce:animate-none" />
      <div className="h-52 animate-pulse rounded-lg border bg-muted/50 motion-reduce:animate-none" />
    </div>
  )
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="flex items-start gap-3">
        <CalendarClock className="h-6 w-6 shrink-0 text-muted-foreground" aria-hidden />
        <div>
          <h2 className="font-semibold">今週の下書きはまだありません</h2>
          <p className="mt-1 text-sm text-muted-foreground">情報源を確認し、更新Skillで下書きを作成してください。</p>
        </div>
      </CardContent>
    </Card>
  )
}

function MissingCurrentWeekState({
  currentWeekOf,
  previousSnapshot,
  onOpenHistory,
}: {
  currentWeekOf: string
  previousSnapshot: MeetingSnapshot
  onOpenHistory: () => void
}) {
  const sourceSuccessfulAt = latestSourceSuccessfulAt(previousSnapshot)
  return (
    <Card>
      <CardContent className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 shrink-0 text-amber-600" aria-hidden />
        <div className="space-y-3">
          <div>
            <h2 className="font-semibold">今週のsnapshotは未作成です</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              対象週は{formatDate(currentWeekOf)}の週です。前回の{formatDate(previousSnapshot.weekOf)}の週を今週として表示していません。
            </p>
          </div>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">前回snapshot保存日時</dt>
              <dd className="tabular-nums">{formatDateTime(previousSnapshot.createdAt)}</dd>
            </div>
            {sourceSuccessfulAt && (
              <div>
                <dt className="text-muted-foreground">前回snapshotの情報源最終成功日時</dt>
                <dd className="tabular-nums">{formatDateTime(sourceSuccessfulAt)}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">前回snapshotの確認済み情報源</dt>
              <dd className="tabular-nums">
                {previousSnapshot.requiredVerifiedSourceCount}/{previousSnapshot.requiredSourceCount}
              </dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={onOpenHistory}
            className="inline-flex min-h-10 items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <History className="h-4 w-4" aria-hidden />
            前回snapshotを履歴で確認
          </button>
        </div>
      </CardContent>
    </Card>
  )
}

function ErrorState({ error }: { error: unknown }) {
  const denied = error instanceof ApiError && error.status === 403
  return (
    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-5 text-sm">
      <div className="flex items-start gap-3">
        {denied
          ? <ShieldAlert className="h-6 w-6 shrink-0 text-destructive" aria-hidden />
          : <XCircle className="h-6 w-6 shrink-0 text-destructive" aria-hidden />}
        <div>
          <h2 className="font-semibold text-destructive">
            {denied ? '会議・報告を閲覧できません' : '会議・報告を取得できませんでした'}
          </h2>
          <p className="mt-1 text-muted-foreground">
            {denied ? '所属と閲覧権限を管理者へ確認してください。' : '接続状態を確認してから再度お試しください。'}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">情報源の鮮度は取得できていません</p>
        </div>
      </div>
    </div>
  )
}

export function MeetingCenterPage({
  surface = 'meeting-center',
}: {
  surface?: MeetingCenterSurface
}) {
  /**
   * 表示対象は現場固定。
   *
   * 「現場 / 役員」の切替は、判断（Top判断）を会議側へ寄せた時点で押しても表示が変わらなく
   * なったため、動かないボタンを残さない判断で撤去した（2026-08-11）。
   * 役員向けの並べ替えを戻すときは、この定数ではなく切替 UI ごと復活させること。
   */
  const audience: MeetingAudience = 'field'
  const [isPresenting, setIsPresenting] = useState(false)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const sourcesTabRef = useRef<HTMLButtonElement>(null)
  const presentationRef = useRef<HTMLDivElement>(null)
  const presentationToggleRef = useRef<HTMLButtonElement>(null)
  const requestedTab = searchParams.get('tab')
  // 通信タブのモック3案（2026-08-07 沖中指示で A〜G から絞り直し）
  const requestedMock = searchParams.get('mock')
  const panelVariant: CohortPanelVariant =
    requestedMock === '2' || requestedMock === '3' ? requestedMock : '1'
  const surfaceCopy = SURFACE_COPY[surface]
  const workspace = useResolvedWorkspace()
  const latestQuery = useMeetingCenterLatest(workspace.workspaceId)
  const response = latestQuery.data
  const latestSnapshot = response?.snapshot
  const responseWeekOf = response?.currentWeekOf
  const currentWeekOf = responseWeekOf && /^\d{4}-\d{2}-\d{2}$/.test(responseWeekOf)
    ? responseWeekOf
    : currentJstWeekOf()
  const snapshotMatchesCurrentWeek = latestSnapshot?.weekOf === currentWeekOf
  const snapshot = latestSnapshot
    && response?.isCurrentWeek !== false
    && snapshotMatchesCurrentWeek
    ? latestSnapshot
    : undefined
  const allowsWorkstreams = surface !== 'evidence-history'
  const meetingTabs = useMeetingTabs()
  /**
   * 専用パネル（数字つき）で描くタブ。どれがそれに当たるかは配信側の tabs 定義が決めるので、
   * 画面コードは会議名を知らない。定義が無い deploy では専用パネルを出さない。
   */
  const panelTab = meetingTabs.find((tab) => tab.renderedElsewhere) ?? null
  const panelTabKey = panelTab?.key ?? ''
  // 取得中は「まだ分からない」であって「無い」ではない。無い側に倒すと既定タブが
  // 一度別のタブへ落ちてから戻るため、初期表示が飛んで見える。
  const panelTabPending = useMeetingTabsLoading()
  // 「今週」は現在の状況に寄せたので週次会議には置かない。
  // 専用パネルのタブは配信側に定義があるときだけ出す（未配置の deploy で空タブを作らない）。
  const allowedBaseTabs: readonly BaseMeetingTab[] = surface === 'weekly-status'
    ? ['current']
    : surface === 'evidence-history'
      ? ['sources', 'history']
      : panelTab || panelTabPending
        ? ['board', 'actions', 'sources', 'history']
        : ['actions', 'sources', 'history']
  // 専用パネルのタブと、それ以外の会議タブを、どちらも取組として合成する
  const snapshotWithBoard = useSnapshotWithWorkstreams(
    useSnapshotWithCohort(snapshot, panelTabKey),
  )
  // 「今週の状況」は専用パネルの会議も他の取組と同じ形で並べる。「週次会議」は資料タブ側で扱う
  const isStatusSurface = surface === 'weekly-status'
  const sourceNotes = useMemo(
    () => Object.fromEntries(meetingTabs.map((tab) => [
      tab.key,
      [tab.sourceState === 'linked' ? null : tab.sourceNote, tab.dailyReportNote]
        .filter(Boolean).join(' ／ ') || null,
    ])),
    [meetingTabs],
  )
  const panelSnapshot = snapshotWithBoard ?? snapshot
  // 会議タブ（業務側）を先に、既存の取組を後に並べる
  const meetingTabOrder = useMemo(() => {
    const order = meetingTabs.filter((tab) => tab.isExtraTab).map((tab) => tab.key)
    // 専用パネルの会議は合成した取組の id が別名
    return ['board-panel', ...order]
  }, [meetingTabs])
  const orderedWorkstreams = useMemo(() => {
    const rank = (id: string) => {
      const index = meetingTabOrder.indexOf(id)
      return index === -1 ? meetingTabOrder.length : index
    }
    return [...(panelSnapshot?.workstreams ?? [])].sort((a, b) => rank(a.workstreamId) - rank(b.workstreamId))
  }, [panelSnapshot, meetingTabOrder])
  // 「週次会議」では専用パネルのタブ側で扱うので、取組タブからは外す
  const tabWorkstreams = allowsWorkstreams
    ? orderedWorkstreams.filter(
        (workstream) => isStatusSurface || workstream.workstreamId !== 'board-panel',
      )
    : []

  const requestedWorkstreamExists = Boolean(
    tabWorkstreams.some(
      (workstream) => workstreamTabValue(workstream.workstreamId) === requestedTab,
    ),
  )
  const defaultTab = allowedBaseTabs.includes(surfaceCopy.defaultTab)
    ? surfaceCopy.defaultTab
    : (allowedBaseTabs[0] ?? 'current')
  const activeTab = allowedBaseTabs.includes(requestedTab as BaseMeetingTab) || requestedWorkstreamExists
    ? requestedTab!
    : defaultTab

  const snapshotSourceState = useMemo(
    () => snapshot ? sourceSummaryState(snapshot) : 'unknown',
    [snapshot],
  )
  const sourceSuccessfulAt = useMemo(
    () => snapshot ? latestSourceSuccessfulAt(snapshot) : null,
    [snapshot],
  )

  useEffect(() => {
    if (!isPresenting) return

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    const focusRestoreTarget = presentationToggleRef.current ?? previouslyFocused
    const previousBodyOverflow = document.body.style.overflow
    const previousPresentationState = document.body.dataset.meetingPresenting
    const chrome = Array.from(document.querySelectorAll<HTMLElement>('[data-app-chrome]'))
      .map((element) => ({
        element,
        ariaHidden: element.getAttribute('aria-hidden'),
        inert: element.inert === true,
      }))

    document.body.style.overflow = 'hidden'
    document.body.dataset.meetingPresenting = 'true'
    for (const state of chrome) {
      state.element.inert = true
      state.element.setAttribute('aria-hidden', 'true')
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setIsPresenting(false)
        return
      }
      if (event.key !== 'Tab') return

      const focusable = Array.from(
        presentationRef.current?.querySelectorAll<HTMLElement>(PRESENTATION_FOCUSABLE_SELECTOR) ?? [],
      ).filter((element) => !element.closest('[hidden],[aria-hidden="true"]'))
      if (focusable.length === 0) {
        event.preventDefault()
        presentationRef.current?.focus()
        return
      }
      const first = focusable[0]!
      const last = focusable.at(-1)!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      } else if (!presentationRef.current?.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    queueMicrotask(() => presentationToggleRef.current?.focus())

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousBodyOverflow
      if (previousPresentationState === undefined) {
        delete document.body.dataset.meetingPresenting
      } else {
        document.body.dataset.meetingPresenting = previousPresentationState
      }
      for (const state of chrome) {
        state.element.inert = state.inert
        if (state.ariaHidden === null) state.element.removeAttribute('aria-hidden')
        else state.element.setAttribute('aria-hidden', state.ariaHidden)
      }
      queueMicrotask(() => {
        if (focusRestoreTarget?.isConnected) focusRestoreTarget.focus()
      })
    }
  }, [isPresenting])

  const changeTab = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'current') next.delete('tab')
    else next.set('tab', value)
    setSearchParams(next, { replace: true })
  }
  const openSources = () => {
    if (surface !== 'evidence-history') {
      navigate('/evidence-history')
      return
    }
    changeTab('sources')
    queueMicrotask(() => sourcesTabRef.current?.focus())
  }
  const copyQuestion = async () => {
    if (!snapshot) return
    try {
      await navigator.clipboard.writeText(buildMeetingQuestionPrompt(snapshot, audience))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  return (
    <div
      ref={presentationRef}
      data-meeting-surface={surface}
      data-presentation-mode={isPresenting ? 'true' : 'false'}
      role={isPresenting ? 'dialog' : undefined}
      aria-modal={isPresenting ? 'true' : undefined}
      aria-label={isPresenting ? '週次会議 投影モード' : undefined}
      tabIndex={isPresenting ? -1 : undefined}
      className={cn(
        'mx-auto w-full max-w-[1440px] space-y-6',
        isPresenting &&
          'fixed inset-0 z-50 max-w-none overflow-y-auto bg-white p-5 text-slate-950 sm:p-8 lg:p-12 [&_.text-muted-foreground]:text-slate-700 [&_.text-sm]:text-base [&_button]:text-base',
      )}
    >
      <header className="space-y-4 border-b pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className={cn('text-2xl font-bold', isPresenting && 'text-3xl lg:text-4xl')}>
              {surfaceCopy.title}
            </h1>
            <p className="mt-1 text-muted-foreground">{surfaceCopy.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {surface === 'meeting-center' && (
              <>
                <button
                  type="button"
                  onClick={() => void copyQuestion()}
                  className="inline-flex min-h-10 items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Copy className="h-4 w-4" aria-hidden />
                  {copyState === 'copied' ? '質問文をコピー済み' : 'AIへの質問文をコピー'}
                </button>
                <button
                  ref={presentationToggleRef}
                  type="button"
                  aria-pressed={isPresenting}
                  onClick={() => setIsPresenting((current) => !current)}
                  className="inline-flex min-h-10 items-center gap-2 rounded-md border bg-card px-3 py-2 text-sm font-medium hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {isPresenting
                    ? <Minimize2 className="h-4 w-4" aria-hidden />
                    : <Maximize2 className="h-4 w-4" aria-hidden />}
                  {isPresenting ? '投影を終了' : '投影モード'}
                </button>
                <span className="sr-only" role="status" aria-live="polite">
                  {copyState === 'failed' ? '質問文をコピーできませんでした' : copyState === 'copied' ? '質問文をコピーしました' : ''}
                </span>
              </>
            )}
          </div>
        </div>

        {/* LOCAL-ONLY: この日付が「何の日付か」を明示する。
            ヘッダーは会議のまとめ（週次snapshot）の日付で、各タブの数字は
            Salesforce や日報の別の日付。同じ画面に混在していて混同の元だった。
            古い場合は隠さず「何日前か」を出す（2026-08-09 沖中判断）。 */}
        {snapshot && (() => {
          const age = daysSince(snapshot.createdAt, new Date())
          const isStale = age !== null && age >= SNAPSHOT_STALE_DAYS
          return (
            <div className="space-y-1.5 text-sm">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span className="text-muted-foreground">会議のまとめ</span>
                <span className="font-medium tabular-nums">{formatDate(snapshot.weekOf)}の週</span>
                <Badge tone={snapshot.lifecycle === 'ready' || snapshot.lifecycle === 'locked' ? 'success' : snapshot.lifecycle === 'blocked' ? 'destructive' : 'warning'} className="rounded-md">
                  {LIFECYCLE_LABELS[snapshot.lifecycle]}
                </Badge>
                {snapshotSourceState !== 'verified' && <StateBadge state={snapshotSourceState} />}
              </div>
              <p className={cn('text-xs tabular-nums', isStale ? 'text-yellow-700' : 'text-muted-foreground')}>
                {formatDateTime(snapshot.createdAt)} 作成
                {age !== null && `（${age === 0 ? '今日' : `${age}日前`}）`}
                {isStale && ' — これ以降、会議のまとめは作られていません'}
              </p>
              <p className="text-xs text-muted-foreground">
                この日付は会議のまとめのものです。各タブの数字はそれぞれのタブに時点を書いています。
              </p>
              <details className="text-xs text-muted-foreground">
                <summary className="inline-flex min-h-8 cursor-pointer items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  取り込みの詳細
                </summary>
                <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 tabular-nums">
                  <div><dt className="inline">snapshot保存日時 </dt><dd className="inline">{formatDateTime(snapshot.createdAt)}</dd></div>
                  {sourceSuccessfulAt && (
                    <div><dt className="inline">情報源最終成功日時 </dt><dd className="inline">{formatDateTime(sourceSuccessfulAt)}</dd></div>
                  )}
                  <div><dt className="inline">改訂 </dt><dd className="inline">{snapshot.revision}</dd></div>
                </dl>
              </details>
            </div>
          )
        })()}
      </header>

      {workspace.isLoading || latestQuery.isLoading ? (
        <LoadingState />
      ) : workspace.isError || latestQuery.isError ? (
        <ErrorState error={latestQuery.error ?? workspace.error} />
      ) : !workspace.workspaceId || !response || !latestSnapshot ? (
        <EmptyState />
      ) : !snapshot ? (
        <div className="space-y-6">
          <MissingCurrentWeekState
            currentWeekOf={currentWeekOf}
            previousSnapshot={latestSnapshot}
            onOpenHistory={() => navigate('/evidence-history?tab=history')}
          />
          {surface === 'evidence-history' && (
            <HistoryPanel workspaceId={workspace.workspaceId} />
          )}
        </div>
      ) : (
        <Tabs.Root value={activeTab} onValueChange={changeTab} orientation="horizontal">
          <Tabs.List
            aria-label="会議・報告の表示内容"
            className="flex gap-1 overflow-x-auto border-b"
          >
            {[
              ...(allowedBaseTabs.includes('current') ? [{ value: 'current', label: '今週' }] : []),
              // 専用パネルのタブは取組タブより前に置く。表示名は配信側の定義に従う
              ...(allowedBaseTabs.includes('board') && panelTab
                ? [{ value: 'board', label: panelTab.label ?? '会議' }]
                : []),
              ...tabWorkstreams.map((workstream) => ({
                value: workstreamTabValue(workstream.workstreamId),
                label: workstream.title,
              })),
              ...(allowedBaseTabs.includes('actions') ? [{ value: 'actions', label: '判断・アクション' }] : []),
              ...(allowedBaseTabs.includes('sources') ? [{ value: 'sources', label: '情報源' }] : []),
              ...(allowedBaseTabs.includes('history') ? [{ value: 'history', label: '履歴' }] : []),
            ].map(({ value, label }) => (
              <Tabs.Trigger
                key={value}
                value={value}
                ref={value === 'sources' ? sourcesTabRef : undefined}
                className="shrink-0 whitespace-nowrap border-b-2 border-transparent px-3 py-3 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring data-[state=active]:border-primary data-[state=active]:text-foreground"
              >
                {label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          {allowedBaseTabs.includes('current') && (
            <Tabs.Content value="current" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <CurrentWeekPanel
                snapshot={panelSnapshot ?? snapshot}
                audience={audience}
                onOpenSources={openSources}
                compact={isStatusSurface}
              />
            </Tabs.Content>
          )}
          {tabWorkstreams.map((workstream) => (
            <Tabs.Content
              key={workstream.workstreamId}
              value={workstreamTabValue(workstream.workstreamId)}
              className="space-y-6 pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {isStatusSurface ? (
                <>
                  {/* LOCAL-ONLY: 数字ではなく「誰が何をやっていて進捗どうか」を見せる取組はこちらを前に出す */}
                  <BoardProgressBoard tab={workstream.workstreamId} />
                  <CompactWorkstreamPanel
                    workstream={workstream}
                    onOpenSources={openSources}
                    sourceNote={sourceNotes[workstream.workstreamId === 'board-panel' ? 'telecom-activation' : workstream.workstreamId]}
                    stalledSince={snapshot?.createdAt ?? null}
                  />
                </>
              ) : (
                <>
                  {/* LOCAL-ONLY: 会議タブの冒頭に、その会議の前回内容を出す */}
                  <BoardPreviousMeetingSection tab={workstream.workstreamId} />
                  <BoardProgressBoard tab={workstream.workstreamId} />
                  <WorkstreamPanel workstream={workstream} onOpenSources={openSources} />
                </>
              )}
            </Tabs.Content>
          ))}
          {/* 会議ボード連携 */}
          <Tabs.Content value="board" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <>
              {/* 案の切替。役員にはここから選んでもらう */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">
                  見せ方の案（どれが良いか選んでください）:
                </span>
                {([
                  { value: 'mock1', label: '案1 議題リスト' },
                  { value: 'mock2', label: '案2 3ブロック' },
                  { value: 'mock3', label: '案3 1画面完結' },
                ] as const).map(({ value, label }) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={panelVariant === value.slice(4)}
                    onClick={() => {
                      const next = new URLSearchParams(searchParams)
                      next.set('tab', 'board')
                      next.set('mock', value.slice(4))
                      next.delete('pattern')
                      setSearchParams(next, { replace: true })
                    }}
                    className={cn(
                      'min-h-9 rounded-md border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      panelVariant === value.slice(4)
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* その会議の冒頭で前回の内容を確認する枠 */}
              <div className="mb-6">
                <BoardPreviousMeetingSection tab={panelTabKey} />
              </div>
              <BoardCohortPanels mock={panelVariant} tab={panelTabKey} />
            </>
          </Tabs.Content>
          <Tabs.Content value="actions" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <DecisionsActionsPanel snapshot={snapshot} onOpenSources={openSources} />
          </Tabs.Content>
          <Tabs.Content value="sources" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <SourcesPanel snapshot={snapshot} />
          </Tabs.Content>
          <Tabs.Content value="history" className="pt-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <HistoryPanel workspaceId={workspace.workspaceId} />
          </Tabs.Content>
        </Tabs.Root>
      )}
    </div>
  )
}
